<template>
  <base-module
    :definition="xyToolDefinitions"
    :connected="xyToolStatus.connected"
    :connecting="xyToolStatus.connecting"
    :error="motorStatus.highTemperature || motorStatus.hasHadHighTemperature"
  >
    <template #title> {{ t('modules.xyTool.index') }} </template>
    <template #config>
      <div
        class="xytool-module__settings"
        @click="changeDevice = true"
      >
        <BfIcon
          icon="/icons/01-Interface Essential/12-Settings/cog.svg"
          :color="Colors.Light"
        />
      </div>
    </template>

    <template #middle>
      <n-form
        v-if="!isCalibrating"
        ref="formRef"
        label-placement="left"
      >
        <n-form-item :label="t('common.bike.saddle')">
          <n-input-group>
            <n-input-group-label>X</n-input-group-label>
            <n-input-number
              :status="editState && !isDirty.saddleX ? 'warning' : undefined"
              v-model:value="saddleXPosition"
              :show-button="false"
              @focus="(setFocusSaddle(), (isDirty.saddleX = true))"
              @blur="setDefault()"
            />
            <n-input-group-label>Y</n-input-group-label>
            <n-input-number
              :status="editState && !isDirty.saddleY ? 'warning' : undefined"
              v-model:value="saddleYPosition"
              :show-button="false"
              @focus="(setFocusSaddle(), (isDirty.saddleY = true))"
              @blur="setDefault()"
            />
          </n-input-group>
        </n-form-item>

        <n-form-item :label="t('common.bike.handlebar')">
          <n-input-group>
            <n-input-group-label>X</n-input-group-label>
            <n-input-number
              :status="editState && !isDirty.handlebarX ? 'warning' : undefined"
              v-model:value="handlebarXPosition"
              :show-button="false"
              @focus="(setFocusHandlebar(), (isDirty.handlebarX = true))"
              @blur="setDefault()"
            />
            <n-input-group-label>Y</n-input-group-label>
            <n-input-number
              :status="editState && !isDirty.handlebarY ? 'warning' : undefined"
              v-model:value="handlebarYPosition"
              :show-button="false"
              @focus="(setFocusHandlebar(), (isDirty.handlebarY = true))"
              @blur="setDefault()"
            />
          </n-input-group>
        </n-form-item>

        <n-form-item :label="getHoodsTerm(bikeHoodsType)">
          <n-input-group>
            <n-input-group-label>X</n-input-group-label>
            <n-input-number
              :status="editState && !isDirty.hoodsX ? 'warning' : undefined"
              v-model:value="hoodsXPosition"
              :show-button="false"
              @focus="(setFocusHoods(), (isDirty.hoodsX = true))"
              @blur="setDefault()"
            />
            <n-input-group-label>Y</n-input-group-label>
            <n-input-number
              :status="editState && !isDirty.hoodsY ? 'warning' : undefined"
              v-model:value="hoodsYPosition"
              :show-button="false"
              @focus="(setFocusHoods(), (isDirty.hoodsY = true))"
              @blur="setDefault()"
            />
          </n-input-group>
        </n-form-item>

        <n-space justify="end">
          <BfButton
            v-if="hasXYTool && (!editState || !hasPositioningMotors)"
            :state="
              xyToolStatus.connected
                ? ButtonState.Enabled
                : ButtonState.Disabled
            "
            @click="sendToXyTool"
          >
            {{ t('modules.xyTool.showOnXy') }}
          </BfButton>
          <BfButton
            v-if="!editState && hasPositioningMotors"
            @click="(setEditState(true), resetDirtyState())"
          >
            {{ t('common.calibrate') }}
          </BfButton>
          <BfButton
            v-if="editState && hasPositioningMotors"
            @click="setEditState(false)"
          >
            {{ t('common.cancel') }}
          </BfButton>
          <BfButton
            v-if="editState || !hasPositioningMotors"
            @click="confirm"
            :state="
              allFieldsDirty || !hasPositioningMotors
                ? ButtonState.Enabled
                : ButtonState.Disabled
            "
          >
            {{
              !hasPositioningMotors ? t('common.measure') : t('common.confirm')
            }}
          </BfButton>
        </n-space>
      </n-form>

      <div
        class="xytool-module__calibrating"
        v-if="isCalibrating && !motorError"
      >
        <BfLoader />
        <p>
          {{ t('modules.xyTool.isCalibrating') }}
        </p>
        <BfButton @click="cancel">{{ t('common.cancel') }}</BfButton>
      </div>

      <n-alert
        :title="t('modules.xyTool.motorError.index')"
        :bordered="false"
        v-if="isCalibrating && motorError"
      >
        <template #icon>
          <Icon
            icon="mdi:information-outline"
            :style="{ fontSize: '24px' }"
          />
        </template>
        <p>
          {{ t('modules.xyTool.motorError.content') }}
        </p>
      </n-alert>

      <div
        v-if="changeDevice"
        class="xymodule-module__select"
      >
        <PairDevices
          :type="deviceType.xyTool"
          @close="changeDevice = false"
        />
      </div>
    </template>
  </base-module>
</template>

<script setup lang="ts">
import {
  NInputNumber,
  NInputGroup,
  NInputGroupLabel,
  NForm,
  NFormItem,
  NAlert,
  NSpace,
} from 'naive-ui'
import { useThrottleFn } from '@vueuse/core'
import {
  BfButton,
  BfIcon,
  BfLoader,
  ButtonState,
  Colors,
} from '@finetic/bikefitting-ui'
import { deviceType } from '@/types/device'
import { PreviewImage } from '@/types/preview'
import { useI18n } from 'vue-i18n'
import { Icon } from '@iconify/vue'
import { useMessage } from 'naive-ui'
import BaseModule from './BaseModule.vue'

import { ref, computed, watch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useMqttStore } from '@/store/mqttStore'
import { usePositionStore } from '@/store/positionStore'
import { useBikeStore } from '@/store/bikeStore'
import { useDeviceStore } from '@/store/deviceStore'
import { usePreferenceStore } from '@/store/preferenceStore'
import { getHoodsTerm } from '@/utils/utils'
import PairDevices from '@/components/controls/PairDevices.vue'

import {
  MqttMessage,
  MqttValue,
  xyToolDefinitions,
  xyToolHandlebarX,
  xyToolHandlebarY,
  xyToolSaddleX,
  xyToolSaddleY,
  xyToolBrakesX,
  xyToolBrakesY,
} from '@/mqttDefinitions'
import { BikeHoodsType } from '@/types/bike'

const { t } = useI18n()
const message = useMessage()

const bikeStore = useBikeStore()
const positionStore = usePositionStore()
const deviceStore = useDeviceStore()
const preferenceStore = usePreferenceStore()

const mqttStore = useMqttStore()
const { doPublish } = mqttStore
const { getMessageOnTopic, client } = storeToRefs(mqttStore)

//Data init
const handlebarX = computed(() => getMessageOnTopic.value(xyToolHandlebarX))
const handlebarY = computed(() => getMessageOnTopic.value(xyToolHandlebarY))
const saddleX = computed(() => getMessageOnTopic.value(xyToolSaddleX))
const saddleY = computed(() => getMessageOnTopic.value(xyToolSaddleY))
const brakesX = computed(() => getMessageOnTopic.value(xyToolBrakesX))
const brakesY = computed(() => getMessageOnTopic.value(xyToolBrakesY))

watch(handlebarX, () => {
  handlebarXPosition.value = handlebarX.value
  isDirty.value.handlebarX = true
})
watch(handlebarY, () => {
  handlebarYPosition.value = handlebarY.value
  isDirty.value.handlebarY = true
})
watch(saddleX, () => {
  saddleXPosition.value = saddleX.value
  isDirty.value.saddleX = true
})
watch(saddleY, () => {
  saddleYPosition.value = saddleY.value
  isDirty.value.saddleY = true
})
watch(brakesX, () => {
  hoodsXPosition.value = brakesX.value
  isDirty.value.hoodsX = true
})
watch(brakesY, () => {
  hoodsYPosition.value = brakesY.value
  isDirty.value.hoodsY = true
})

const changeDevice = ref(false)
const editState = ref(false)

const { bikeData, bikeHoodsType, bikePreview } = storeToRefs(bikeStore)
const { hasPositioningMotors, hasXYTool } = storeToRefs(preferenceStore)
const {
  initialCalibrate,
  updatedCalibrate,
  noMotorsCalibrate,
  cancelCalibration,
} = positionStore
const { positionDeviation, isCalibrating, isCalibrated } =
  storeToRefs(positionStore)
const { xyToolStatus, motorStatus } = storeToRefs(deviceStore)

const hoodsXPosition = ref(0)
const hoodsYPosition = ref(0)

const handlebarXPosition = computed({
  get() {
    return positionDeviation.value.handlebarX
  },
  set(newValue) {
    positionDeviation.value.handlebarX = newValue
  },
})

const handlebarYPosition = computed({
  get() {
    return positionDeviation.value.handlebarY
  },
  set(newValue) {
    positionDeviation.value.handlebarY = newValue
  },
})

const saddleXPosition = computed({
  get() {
    return positionDeviation.value.saddleX
  },
  set(newValue) {
    positionDeviation.value.saddleX = newValue
  },
})

const saddleYPosition = computed({
  get() {
    return positionDeviation.value.saddleY
  },
  set(newValue) {
    positionDeviation.value.saddleY = newValue
  },
})

const isDirty = ref({
  handlebarX: false,
  handlebarY: false,
  saddleX: false,
  saddleY: false,
  hoodsX: false,
  hoodsY: false,
})

const allFieldsDirty = computed(() => {
  return Object.values(isDirty.value).every((value) => value)
})

const setEditState = (value: boolean) => {
  editState.value = value
  if (!value) {
    resetDirtyState()
  }
}

const setFocusHoods = () => {
  switch (bikeHoodsType.value) {
    case BikeHoodsType.DROPBAR:
      bikePreview.value = PreviewImage.DROPBAR_HOODS
      break

    case BikeHoodsType.FLATBAR:
      bikePreview.value = PreviewImage.FLATBAR_GRIPS
      break

    case BikeHoodsType.TRIBAR:
      bikePreview.value = PreviewImage.TRIBAR_PADS
      break

    default:
      bikePreview.value = PreviewImage.DROPBAR_HOODS
      break
  }
}

const setFocusHandlebar = () => {
  switch (bikeHoodsType.value) {
    case BikeHoodsType.DROPBAR:
      bikePreview.value = PreviewImage.DROPBAR_HANDLEBAR
      break

    case BikeHoodsType.FLATBAR:
      bikePreview.value = PreviewImage.FLATBAR_HANDLEBAR
      break

    case BikeHoodsType.TRIBAR:
      bikePreview.value = PreviewImage.TRIBAR_HANDLEBAR
      break

    default:
      bikePreview.value = PreviewImage.DROPBAR_HANDLEBAR
      break
  }
}

const setFocusSaddle = () => {
  bikePreview.value = PreviewImage.SADDLE
}

const setDefault = () => {
  switch (bikeHoodsType.value) {
    case BikeHoodsType.DROPBAR:
      bikePreview.value = PreviewImage.DROPBAR_BIKE
      break

    case BikeHoodsType.FLATBAR:
      bikePreview.value = PreviewImage.FLATBAR_BIKE
      break

    case BikeHoodsType.TRIBAR:
      bikePreview.value = PreviewImage.TRIBAR_BIKE
      break

    default:
      bikePreview.value = PreviewImage.DROPBAR_BIKE
      break
  }
}

const confirm = async () => {
  const success = hasPositioningMotors.value
    ? !isCalibrated.value
      ? await initialCalibrate()
      : await updatedCalibrate()
    : await noMotorsCalibrate()

  if (success) {
    editState.value = false
    resetDirtyState()
  } else {
    message.error(t('setup.calibrate.outOfReach'))
  }
}

const cancel = () => {
  editState.value = false
  cancelCalibration()
}

const sendToXyTool = () => {
  let index = 0

  const xyData: Array<{ mqtt: MqttValue; position: any }> = [
    { mqtt: xyToolHandlebarX, position: handlebarXPosition },
    { mqtt: xyToolHandlebarY, position: handlebarYPosition },
    { mqtt: xyToolSaddleX, position: saddleXPosition },
    { mqtt: xyToolSaddleY, position: saddleYPosition },
    { mqtt: xyToolBrakesX, position: hoodsXPosition },
    { mqtt: xyToolBrakesY, position: hoodsYPosition },
  ]

  const throttledDoPublish = useThrottleFn(
    (item: { mqtt: MqttValue; position: any }) => {
      doPublish(
        {
          topic: item.mqtt.topic,
          payload: item.position.value,
        } as MqttMessage,
        item.mqtt,
        client.value,
      )
    },
    200,
  )

  const processNext = () => {
    if (index < xyData.length) {
      throttledDoPublish(xyData[index])
      index++
      setTimeout(processNext, 200)
    }
  }

  processNext()
}

const resetValues = async () => {
  if (!hasPositioningMotors.value && bikeData?.value) {
    positionDeviation.value.handlebarX = bikeData.value.SF_HANDLEBAR_CENTER_X
    positionDeviation.value.handlebarY = bikeData.value.SF_HANDLEBAR_CENTER_Y
    positionDeviation.value.saddleX = bikeData.value.SF_SADDLE_CENTER_X
    positionDeviation.value.saddleY = bikeData.value.SF_SADDLE_CENTER_Y
    await confirm()
  }
}

const resetDirtyState = () => {
  isDirty.value = {
    handlebarX: false,
    handlebarY: false,
    saddleX: false,
    saddleY: false,
    hoodsX: false,
    hoodsY: false,
  }
}

const motorError = computed(() => {
  return (
    motorStatus.value.overcurrent ||
    motorStatus.value.stalled ||
    motorStatus.value.highTemperature
  )
})

onMounted(() => {
  resetValues()
})
</script>

<style lang="scss" scoped>
.xytool-module {
  &__settings {
    opacity: 0.6;
    cursor: pointer;

    &:hover {
      opacity: 1;
    }
  }

  &__calibrating {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin-top: 32px;

    p {
      text-align: center;
      margin-top: 12px;
    }
  }
}

.motorcontrols {
  width: 100%;
}

.n-form-item.n-form-item--left-labelled {
  grid-template-columns: 100px minmax(0, 1fr);

  .n-form-item-label {
    max-width: 100px;
  }
}
</style>
